import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2, MapPin, AlertCircle, Check, Heart, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHeart } from '@/context/HeartContext';
import { cityConfig } from '@/config/cities';
import { checkHeart, uploadFile, submitHeart, detectNumber } from '@/lib/api';
import { compressImage } from '@/lib/imageCompress';
import { toast } from 'sonner';

const CITY_PLACES = cityConfig.places || [];

// Reverse geocode: lat/lon → street name via Photon
const reverseGeocode = async (lat, lon) => {
  try {
    const resp = await fetch(`https://photon.komoot.io/reverse?lat=${lat}&lon=${lon}&lang=fr`);
    const data = await resp.json();
    if (data.features && data.features.length > 0) {
      const p = data.features[0].properties;
      const parts = [];
      if (p.housenumber) parts.push(p.housenumber);
      if (p.street) parts.push(p.street);
      else if (p.name) parts.push(p.name);
      if (p.district) parts.push(p.district);
      else if (p.city) parts.push(p.city);
      return parts.join(', ') || null;
    }
  } catch (e) { console.error('Reverse geocode failed:', e); }
  return null;
};

const DiscoveryFlow = () => {
  const navigate = useNavigate();
  const { setLastResult, resetSubmission } = useHeart();
  const [step, setStep] = useState(1);

  // Device identification — persistent UUID
  const [deviceId] = useState(() => {
    let id = localStorage.getItem('hearts_device_id');
    if (!id) { id = crypto.randomUUID(); localStorage.setItem('hearts_device_id', id); }
    return id;
  });

  // Step 1: Photo + AI detection
  const [closePhotoPath, setClosePhotoPath] = useState(null);
  const [closePreview, setClosePreview] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectedNumber, setDetectedNumber] = useState(null);
  const [heartNumber, setHeartNumber] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [detectionFailed, setDetectionFailed] = useState(false);
  const [alreadyDiscovered, setAlreadyDiscovered] = useState(null); // { number, pseudo }
  const [detectionAttempts, setDetectionAttempts] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [verifiedByAI, setVerifiedByAI] = useState(false);
  const [error, setError] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const photoRef = useRef(null);

  // Pseudo — pre-filled from localStorage
  const [pseudo, setPseudo] = useState(() => localStorage.getItem('hearts_pseudo') || '');

  // Location (auto-filled via GPS + reverse geocoding)
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoSource, setGeoSource] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const debounceRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => { resetSubmission(); }, [resetSubmission]);

  // Auto-locate early: start when photo detected or on final step
  useEffect(() => {
    if ((detectedNumber || step === 2) && !latitude) autoLocate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedNumber, step]);

  const handlePhotoCapture = async (file) => {
    if (!file) return;
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    const ok = file.type.startsWith('image/') || ['jpg','jpeg','png','gif','webp','heic','heif'].includes(ext);
    if (!ok) { toast.error('Seules les images sont acceptées'); return; }
    try {
      setDetecting(true); setError(''); setManualMode(false); setDetectedNumber(null); setAlreadyDiscovered(null);
      // Aperçu instantané via blob URL
      const localPreview = URL.createObjectURL(file);
      setClosePreview(localPreview);
      const compressed = await compressImage(file);
      const result = await uploadFile(compressed);
      setClosePhotoPath(result.path);
      const detection = await detectNumber(result.path);
      if (detection.status === 'blurry') {
        // Photo floue — demander de reprendre, ne compte pas comme tentative
        setError('Photo trop floue. Reprends la photo avec le cœur bien net.');
        setDetectionFailed(true);
        return;
      }
      if (detection.status === 'absent') {
        // Pas de cœur LEGO avec numéro visible — demander de reprendre
        setError('Aucun cœur avec numéro détecté. Cadre bien le numéro écrit au marqueur.');
        setDetectionFailed(true);
        return;
      }
      if (detection.status === 'detected' && detection.detected) {
        setDetectionFailed(false);
        // Check if already discovered BEFORE showing confirmation
        try {
          const check = await checkHeart(detection.detected);
          if (check.discovered) {
            setAlreadyDiscovered({ number: detection.detected, pseudo: check.heart.pseudo });
            return;
          }
        } catch (e) { console.error('Heart check failed:', e); }
        // Heart is available — show confirmation
        setDetectedNumber(detection.detected);
        setHeartNumber(String(detection.detected));
        setConfirmed(false);
      } else {
        // Detection failed
        const newAttempts = detectionAttempts + 1;
        setDetectionAttempts(newAttempts);
        if (newAttempts >= 2) {
          // 2e échec → saisie manuelle directe
          setManualMode(true);
          setDetectionFailed(false);
        } else {
          // 1er échec → proposer de reprendre la photo
          setDetectionFailed(true);
        }
      }
    } catch (e) {
      console.error('Photo upload/detection failed:', e);
      const newAttempts = detectionAttempts + 1;
      setDetectionAttempts(newAttempts);
      if (newAttempts >= 2) {
        setManualMode(true);
        setDetectionFailed(false);
      } else {
        setDetectionFailed(true);
      }
    } finally { setDetecting(false); }
  };

  const handleConfirmNumber = async () => {
    setError('');
    const num = parseInt(heartNumber, 10);
    if (isNaN(num) || num < 1 || num > cityConfig.totalHearts) { setError(`Numéro entre 1 et ${cityConfig.totalHearts}`); return; }
    setCheckLoading(true);
    try {
      const result = await checkHeart(num);
      if (result.discovered) { setError(`Déjà découvert par ${result.heart.pseudo}`); }
      else { setVerifiedByAI(false); setStep(2); }
    } catch (e) {
      console.error('Heart check failed:', e);
      const msg = !e.response
        ? 'Connexion instable, réessaye'
        : "Erreur de connexion";
      toast.error(msg);
    }
    finally { setCheckLoading(false); }
  };

  const handleConfirmDetected = async () => {
    setError('');
    setConfirmed(true);
    setVerifiedByAI(true);
    setStep(2);
  };
  // Auto-locate: GPS → reverse geocode → auto-fill address
  const autoLocate = async () => {
    setGeoLoading(true);
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 });
        });
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        setLatitude(lat); setLongitude(lon);
        const address = await reverseGeocode(lat, lon);
        if (address) setLocationName(address);
        setGeoSource('gps'); setGeoLoading(false); return;
      } catch (e) { console.error('GPS failed:', e); }
    }
    // Fallback — just stop loading, let user type
    setGeoLoading(false);
  };

  const searchPhoton = useCallback(async (query) => {
    try {
      const numMatch = query.match(/^(\d+[\s,]*)(.*)/);
      const typedNumber = numMatch ? numMatch[1].trim() : '';
      const searchText = numMatch ? numMatch[2].trim() : query.trim();
      if (searchText.length < 2) return [];
      const params = new URLSearchParams({
        q: searchText,
        lat: String(cityConfig.photonCenter.lat),
        lon: String(cityConfig.photonCenter.lon),
        limit: '8',
        lang: 'fr',
        bbox: cityConfig.photonBbox,
      });
      if (/^(rue|av|bou|pla|pas|imp|all)/i.test(searchText)) params.set('osm_tag', 'highway');
      const resp = await fetch(`https://photon.komoot.io/api/?${params}`);
      const data = await resp.json();
      return (data.features || []).map(f => {
        const p = f.properties;
        const name = p.name || '';
        const street = p.street || '';
        const district = p.district || p.city || '';
        const parts = [name || street, district].filter(Boolean);
        let display = parts.join(', ') || searchText;
        // Prepend the typed number to the suggestion
        if (typedNumber && !/^\d/.test(display)) display = `${typedNumber} ${display}`;
        return { display, lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0], source: 'photon' };
      }).slice(0, 6);
    } catch (e) { console.error('Photon search failed:', e); return []; }
  }, []);

  const handleLocationChange = (value) => {
    setLocationName(value);
    const searchPart = value.replace(/^\d+[\s,]*/, '').trim().toLowerCase();
    const local = searchPart.length >= 1
      ? CITY_PLACES.filter(p => p.name.toLowerCase().startsWith(searchPart) || p.name.toLowerCase().includes(searchPart)).slice(0, 3)
          .map(p => ({ display: p.name, lat: p.lat, lon: p.lon, source: 'local' }))
      : [];
    setSuggestions(local); setShowSuggestions(local.length > 0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchPart.length >= 2) {
      setSearching(true);
      debounceRef.current = setTimeout(async () => {
        const remote = await searchPhoton(value);
        const names = new Set(local.map(l => l.display.toLowerCase()));
        setSuggestions([...local, ...remote.filter(r => !names.has(r.display.toLowerCase()))].slice(0, 6));
        setShowSuggestions(true); setSearching(false);
      }, 250);
    }
  };

  const selectSuggestion = (s) => {
    const typed = locationName.trim();
    const numMatch = typed.match(/^(\d+[\s,]*)/);
    let finalName = s.display;
    if (numMatch && !/^\d/.test(s.display)) finalName = `${numMatch[1].replace(/[,\s]+$/, '')} ${s.display}`;
    setLocationName(finalName);
    setLatitude(s.lat); setLongitude(s.lon); setGeoSource('manual');
    setShowSuggestions(false);
  };

  const handlePublish = async () => {
    const trimmedPseudo = pseudo.trim();
    if (!trimmedPseudo || trimmedPseudo.length < 1) { setError('Choisis un pseudo'); return; }
    if (!latitude && !locationName.trim()) { setError('Indique où tu as trouvé ce cœur'); return; }
    setError('');
    setSubmitLoading(true);
    try {
      const result = await submitHeart({
        heartNumber: parseInt(heartNumber, 10), pseudo: trimmedPseudo, email: '',
        closePhotoPath, contextPhotoPath: null, latitude, longitude, locationName: locationName.trim(),
        deviceId, verifiedByAI,
      });
      // Save pseudo to localStorage for next time
      localStorage.setItem('hearts_pseudo', trimmedPseudo);
      setLastResult(result);
      localStorage.setItem('lastDiscovery', JSON.stringify({ result, photoUrl: closePreview }));
      navigate('/success');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitLoading(false); }
  };

  return (
    <div
      className="fixed top-14 bottom-[60px] left-0 right-0 flex flex-col px-4 overflow-y-auto overflow-x-hidden z-30"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)', maxWidth: '100vw', background: '#000000' }}
      data-testid="discovery-flow"
    >
      <div className="max-w-lg mx-auto w-full flex flex-col flex-1">

        {/* ── BANNERS — hidden on step 2, integrated directly ── */}
        {step > 2 && (
          <div className="shrink-0 py-2">
            <div className="flex items-center gap-2.5 px-1" data-testid="step1-summary">
              {closePreview && <img src={closePreview} alt="" className="w-7 h-7 object-cover flex-shrink-0 rounded" />}
              <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
              <span className="text-[#E63946] font-display font-bold text-sm">#{heartNumber}</span>
              <div className="flex-1 h-px bg-white/5" />
            </div>
          </div>
        )}

        {/* ── ACTIVE STEP ── */}
        <div className="flex-1 flex flex-col justify-start pt-3">
          <AnimatePresence mode="wait">

            {/* ═══ STEP 1: Photo + AI Detection ═══ */}
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col flex-1">
                {!closePreview ? (
                  <>
                    {/* Instruction courte + bouton */}
                    <div className="flex flex-col items-center justify-center flex-1 gap-8">
                      <p className="text-white/60 text-base text-center">
                        Photographie le cœur de près<br />avec le numéro visible
                      </p>

                      <button
                        onClick={() => photoRef.current?.click()}
                        className="w-32 h-32 rounded-full bg-[#E63946] flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-all duration-200"
                        style={{ boxShadow: '0 0 60px rgba(230, 57, 70, 0.35)' }}
                        data-testid="main-photo-capture"
                      >
                        <Camera className="w-14 h-14" />
                      </button>

                      <p className="text-white/30 text-xs">Le numéro sera détecté automatiquement</p>
                    </div>
                    <input ref={photoRef} type="file" accept="image/*,.heic,.heif" capture="environment" onChange={(e) => { if (e.target.files[0]) handlePhotoCapture(e.target.files[0]); }} className="hidden" />
                  </>
                ) : detecting ? (
                  /* ═══ Analyse en cours — photo hero + spinner subtil ═══ */
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="flex flex-col items-center flex-1 -mx-4">
                    <div className="relative w-full bg-black" style={{ height: '50vh', maxHeight: '420px' }}>
                      <img src={closePreview} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" style={{ opacity: closePreview ? 1 : 0 }} />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 50%, #000000 100%)' }} />
                    </div>
                    <div className="flex items-center gap-2.5 mt-6 text-sm text-white/50">
                      <Loader2 className="w-4 h-4 animate-spin text-[#E63946]" />
                      <span>Analyse en cours...</span>
                    </div>
                  </motion.div>
                ) : detectionFailed ? (
                  /* ═══ Détection échouée — reprendre directement ═══ */
                  <div className="flex flex-col items-center justify-center flex-1 gap-6">
                    {closePreview && (
                      <div className="w-44 h-44 overflow-hidden rounded-xl border border-white/10">
                        <img src={closePreview} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-white/60 text-sm text-center max-w-[280px]">
                      {error || "Numéro non identifié. Reprends la photo avec le numéro bien visible"}
                    </p>
                    <button
                      onClick={() => { setClosePhotoPath(null); setClosePreview(null); setDetectedNumber(null); setDetectionFailed(false); setConfirmed(false); setHeartNumber(''); setError(''); setTimeout(() => photoRef.current?.click(), 100); }}
                      className="w-32 h-32 rounded-full bg-[#E63946] flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-all duration-200"
                      style={{ boxShadow: '0 0 60px rgba(230, 57, 70, 0.35)' }}
                      data-testid="retake-photo-button"
                    >
                      <Camera className="w-14 h-14" />
                    </button>
                  </div>
                ) : alreadyDiscovered ? (
                  /* ═══ Cœur déjà découvert — écran dédié ═══ */
                  <div className="flex flex-col items-center flex-1 -mx-4">
                    {/* Photo hero */}
                    <div className="relative w-full" style={{ height: '36vh', maxHeight: '300px' }}>
                      <img src={closePreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 50%, #000000 100%)' }} />
                    </div>

                    <div className="flex flex-col items-center px-6 w-full mt-4">
                      <span className="font-display font-bold text-white/20" style={{ fontSize: 'clamp(2rem, 9vw, 3rem)', lineHeight: 1 }}>#{alreadyDiscovered.number}</span>
                      <p className="text-white/50 text-sm text-center mt-2">Déjà découvert par <span className="text-white font-semibold">{alreadyDiscovered.pseudo}</span></p>

                      <div className="w-full max-w-xs flex flex-col items-center gap-3 mt-10">
                        <button
                          onClick={() => { setClosePhotoPath(null); setClosePreview(null); setDetectedNumber(null); setDetectionFailed(false); setAlreadyDiscovered(null); setConfirmed(false); setHeartNumber(''); setError(''); setDetectionAttempts(0); setTimeout(() => photoRef.current?.click(), 100); }}
                          className="w-full text-white font-semibold py-4 flex items-center justify-center gap-2.5 transition-all duration-300"
                          style={{ background: '#E63946', borderRadius: '14px', boxShadow: '0 8px 40px rgba(230, 57, 70, 0.35)', fontSize: '15px' }}
                          data-testid="scan-another-button"
                        >
                          <Camera className="w-5 h-5" />
                          Scanner un autre cœur
                        </button>
                        <button onClick={() => navigate('/')}
                          className="text-white/25 hover:text-white/40 text-xs transition-colors py-1"
                          data-testid="back-home-button">
                          Retour à l'accueil
                        </button>
                      </div>
                    </div>
                  </div>
                ) : detectedNumber && !confirmed ? (
                  /* ═══ Numéro détecté — flux continu serré ═══ */
                  <div className="flex flex-col items-center flex-1 justify-center -mx-4">
                    {/* Photo edge-to-edge */}
                    <div className="relative w-full" style={{ height: '40vh', maxHeight: '340px' }}>
                      <img src={closePreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 55%, #000000 100%)' }} />
                    </div>

                    {/* Contenu — flux continu, tout centré */}
                    <div className="flex flex-col items-center px-6 w-full">
                      <span className="font-display font-bold text-[#E63946] mt-4" style={{ fontSize: 'clamp(2rem, 9vw, 3rem)', lineHeight: 1 }} data-testid="detected-number">#{detectedNumber}</span>
                      <p className="text-white/30 text-[11px] tracking-[0.2em] uppercase mt-1.5">C'est bien ton cœur ?</p>

                      {error && <p className="text-[#E63946] text-xs flex items-center gap-1.5 mt-3"><AlertCircle className="w-3.5 h-3.5" />{error}</p>}

                      <button onClick={handleConfirmDetected} disabled={checkLoading}
                        className="w-full max-w-xs text-white font-semibold py-4 flex items-center justify-center transition-all duration-300 mt-7"
                        style={{ background: '#E63946', borderRadius: '14px', boxShadow: '0 8px 40px rgba(230, 57, 70, 0.35)', fontSize: '15px' }}
                        data-testid="confirm-number-button">
                        {checkLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Oui, c'est le bon"}
                      </button>
                      <button onClick={() => { setManualMode(true); setHeartNumber(''); setDetectedNumber(null); }}
                        className="text-white/25 hover:text-white/40 text-xs transition-colors mt-2.5"
                        data-testid="wrong-number-button">
                        Non, corriger le numéro
                      </button>
                    </div>
                  </div>
                ) : manualMode ? (
                  /* ═══ Correction manuelle — même style que Step 2 ═══ */
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex flex-col items-center flex-1 -mx-4">

                    {/* Photo + gradient */}
                    <div className="relative w-full" style={{ height: '38vh', maxHeight: '320px' }}>
                      <img src={closePreview} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, #000000 95%)' }} />
                    </div>

                    {/* Message + Input — centré, aéré */}
                    <p className="text-white/50 text-base text-center mt-6 px-6 mb-6">Numéro non identifié<br/>Renseigne-le ci-dessous</p>

                    <div className="w-full px-6">
                      {/* Input large, centré */}
                      <div className="flex items-center justify-center gap-2 mb-6">
                        <span className="text-[#E63946] font-bold" style={{ fontSize: '2.5rem' }}>#</span>
                        <input
                          type="number"
                          value={heartNumber}
                          onChange={(e) => setHeartNumber(e.target.value)}
                          placeholder="—"
                          min="1"
                          max={String(cityConfig.totalHearts)}
                          className="bg-transparent text-white font-bold text-center focus:outline-none placeholder:text-white/15"
                          style={{ fontSize: '2.5rem', width: '4ch', caretColor: '#E63946', MozAppearance: 'textfield' }}
                          data-testid="manual-number-input"
                          autoFocus
                        />
                      </div>

                      {error && <p className="text-[#E63946] text-xs flex items-center gap-1.5 mb-3 justify-center"><AlertCircle className="w-3.5 h-3.5" />{error}</p>}

                      <button
                        onClick={handleConfirmNumber}
                        disabled={checkLoading || !heartNumber}
                        className="w-full text-white font-semibold py-4 flex items-center justify-center transition-all duration-300 disabled:opacity-30"
                        style={{
                          background: '#E63946',
                          borderRadius: '14px',
                          boxShadow: '0 8px 40px rgba(230, 57, 70, 0.35)',
                          fontSize: '15px',
                        }}
                        data-testid="confirm-manual-button"
                      >
                        {checkLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirmer"}
                      </button>
                    </div>
                  </motion.div>
                ) : null}
              </motion.div>
            )}

            {/* ═══ STEP 2: Location + Pseudo + Publier ═══ */}
            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex flex-col items-center flex-1 -mx-4">

                {/* Photo réduite + numéro inline — visuellement DIFFÉRENT du step 1 */}
                <div className="flex items-center gap-4 mt-4 mb-2 px-5">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-white/10">
                    <img src={closePreview} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <span className="font-display font-bold text-[#E63946] text-xl">#{heartNumber}</span>
                    <p className="text-white/30 text-[10px] tracking-wider uppercase">{verifiedByAI ? 'Vérifié' : 'Déclaré'}</p>
                  </div>
                </div>

                {/* Titre clair — l'utilisateur sait ce qu'il doit faire */}
                <p className="text-white text-lg font-semibold mt-4 mb-5">Où l'as-tu trouvé ?</p>

                {/* Formulaire */}
                <div className="w-full flex flex-col items-center flex-1 px-6">

                  {/* Localisation */}
                  {geoLoading ? (
                    <div className="flex items-center gap-2 mb-4">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E63946]" />
                      <span className="text-white/30 text-sm">Localisation...</span>
                    </div>
                  ) : geoSource === 'gps' && locationName && geoSource !== 'manual' ? (
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                      <span className="text-white text-sm">{locationName}</span>
                      <button onClick={() => setGeoSource('manual')} className="text-white/20 hover:text-white/40 text-[10px] uppercase tracking-wider" data-testid="change-location-button">modifier</button>
                    </div>
                  ) : (
                    <div className="relative w-full mb-4">
                      <div className="flex items-center gap-3 rounded-xl py-3 px-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <MapPin className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                        <input type="text" value={locationName} onChange={(e) => handleLocationChange(e.target.value)} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 250)} placeholder={cityConfig.searchPlaceholder} className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder:text-white/40" data-testid="manual-location-input" autoComplete="off" autoFocus />
                      </div>
                      {showSuggestions && (
                        <div className="absolute z-10 w-full mt-1 bg-[#111] border border-white/10 max-h-44 overflow-y-auto rounded-xl" data-testid="location-suggestions">
                          {suggestions.map((s, i) => (
                            <button key={`${s.source}-${i}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => selectSuggestion(s)} className="w-full px-4 py-2.5 text-left text-white hover:bg-white/5 flex items-center gap-2.5 border-b border-white/5 last:border-0 text-sm">
                              <MapPin className="w-3.5 h-3.5 text-[#E63946] flex-shrink-0" /><span className="truncate">{s.display}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pseudo */}
                  <div className="flex items-center gap-3 rounded-xl py-3 px-4 w-full" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <User className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                    <input type="text" value={pseudo} onChange={(e) => setPseudo(e.target.value.toUpperCase())} placeholder="Ton pseudo" maxLength={30} className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder:text-white/40 uppercase tracking-wide" data-testid="pseudo-input" />
                  </div>

                  {error && <p className="text-[#E63946] text-xs flex items-center gap-1.5 mt-3"><AlertCircle className="w-3.5 h-3.5" />{error}</p>}

                  <button onClick={handlePublish} disabled={submitLoading}
                    className="w-full max-w-xs text-white font-semibold py-4 flex items-center justify-center transition-all duration-300 disabled:opacity-30 mt-6"
                    style={{ background: '#E63946', borderRadius: '14px', boxShadow: '0 8px 40px rgba(230, 57, 70, 0.35)', fontSize: '15px' }}
                    data-testid="submit-heart-button">
                    {submitLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Publier ma découverte"}
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryFlow;
